import type { Event, EventHint } from '@sentry/react';

type ChainableSentryBeforeSendFilter = (event: Event, hint?: EventHint) => PromiseLike<Event | null> | Event | null;

const chainFilters =
  (filters: ChainableSentryBeforeSendFilter[]): ChainableSentryBeforeSendFilter =>
  (event, hint) => {
    return filters.reduce<PromiseLike<Event | null> | Event | null>((result, filter) => {
      if (result === null) {
        return null;
      }

      return filter(event, hint);
    }, event);
  };

const excludeIllegalInvocation: ChainableSentryBeforeSendFilter = (event) => {
  const theException = event.exception?.values?.find(
    (exception) => exception.stacktrace?.frames?.find((frame) => frame.filename?.includes('/gtag/js')),
  );

  if (theException?.value?.match?.(/Illegal invocation/i)) {
    return null;
  }

  return event;
};

const excludeGTM: ChainableSentryBeforeSendFilter = (event) => {
  const isGTMEvent =
    event.exception?.values?.some(
      (exception) => exception.stacktrace?.frames?.find((frame) => frame.filename?.includes('/gtm.js')),
    ) ?? false;

  if (isGTMEvent) {
    return null;
  }

  return event;
};

const excludeUndefinedDataLayer: ChainableSentryBeforeSendFilter = (event) => {
  if (event.exception?.values?.[0]?.value === 'dataLayer is not defined') {
    return null;
  }

  return event;
};

const excludeDomains =
  (domains: string[]): ChainableSentryBeforeSendFilter =>
  (event, hint) => {
    if (
      event.exception?.values?.find(
        (exception) =>
          exception.stacktrace?.frames?.find((frame) =>
            domains.some((domain) => {
              if (frame.filename === undefined) {
                return false;
              }

              try {
                const url = new URL(frame.filename);

                return url.origin.includes(domain);
              } catch (e) {
                return frame.filename?.includes(domain);
              }
            }),
          ),
      )
    ) {
      return null;
    }

    if (hint !== undefined) {
      try {
        const filenameAndFunction = event.exception?.values?.map((exception) =>
          (exception.stacktrace?.frames ?? []).map(({ filename, function: func }) => ({ filename, func })),
        );
        hint.attachments = [
          ...(hint.attachments ?? []),
          { filename: 'filenameAndFunction.json', data: JSON.stringify(filenameAndFunction, null, 2) },
        ];
      } catch (e) {
        // Do nothing
      }
    }

    return event;
  };

export { chainFilters, excludeIllegalInvocation, excludeUndefinedDataLayer, excludeDomains, excludeGTM };
